/**
 * PATH: packages/quote-builder/src/api/quotes/notesFiles.ts
 * FILE_ID: QB-API-QUOTES-NOTESFILES-001
 * PURPOSE: Notes drawer CRUD + Files drawer (quote attachments with
 *          upload validation, fresh presigned URLs) + the QDrive picker
 *          bridge endpoints.
 * WHY: Split by domain from the former 1.1k-line quotesApi.ts during
 *      the A3 lift; see ./core.ts for the module map.
 */

import { qbApi } from '../client'

const base = '/api/quotebuilder/quotes'

// ---- Notes (Notes drawer) ----

export type QuoteNoteVisibility = 'internal' | 'client' | 'personal'

export interface QuoteNote {
  id: string
  body: string
  visibility: QuoteNoteVisibility
  pinned: boolean
  author: number | null
  author_name: string | null
  created_at: string
  updated_at: string
  context_quote_number?: string | null
  context_revised_from?: string | null
}

export async function listNotes(
  orgId: string,
  quoteId: string,
  visibility?: QuoteNoteVisibility,
): Promise<QuoteNote[]> {
  const query = new URLSearchParams({ org_id: orgId })
  if (visibility) query.set('visibility', visibility)
  const res = await qbApi().get(`${base}/${quoteId}/notes/?${query.toString()}`)
  return Array.isArray(res.data) ? res.data : res.data?.results || []
}

export async function createNote(
  orgId: string,
  quoteId: string,
  payload: { body: string; visibility?: QuoteNoteVisibility; pinned?: boolean },
): Promise<QuoteNote> {
  const res = await qbApi().post(`${base}/${quoteId}/notes/?org_id=${orgId}`, payload)
  return res.data
}

export async function updateNote(
  orgId: string,
  quoteId: string,
  noteId: string,
  updates: { body?: string; visibility?: QuoteNoteVisibility; pinned?: boolean },
): Promise<QuoteNote> {
  const res = await qbApi().patch(
    `${base}/${quoteId}/notes/${noteId}/?org_id=${orgId}`,
    updates,
  )
  return res.data
}

export async function deleteNote(orgId: string, quoteId: string, noteId: string): Promise<void> {
  await qbApi().delete(`${base}/${quoteId}/notes/${noteId}/?org_id=${orgId}`)
}

// ---- Files (Files drawer) ----

/** Attachment categories — mirrors QuoteAttachment.TYPE_CHOICES on the backend. */
export type QuoteFileCategory = 'file' | 'drawing' | 'photo' | 'specification' | 'other'

export const QUOTE_FILE_CATEGORY_LABELS: Record<QuoteFileCategory, string> = {
  file: 'General',
  drawing: 'Drawings',
  photo: 'Photos',
  specification: 'Specifications',
  other: 'Other',
}

export const QUOTE_ATTACHMENT_MAX_MB = 20
export const QUOTE_ATTACHMENT_MAX_BYTES = QUOTE_ATTACHMENT_MAX_MB * 1024 * 1024
// Any extension is allowed EXCEPT web-executable types (mirrors the backend
// blocklist in QuoteAttachmentUploadSerializer).
export const QUOTE_ATTACHMENT_BLOCKED_EXTENSIONS = [
  'html', 'htm', 'xhtml', 'css', 'js', 'mjs', 'cjs',
] as const

function quoteAttachmentExtension(fileName: string): string {
  const parts = fileName.trim().toLowerCase().split('.')
  return parts.length > 1 ? parts[parts.length - 1] : ''
}

export function validateQuoteAttachmentFile(file: File): string | null {
  if (file.size > QUOTE_ATTACHMENT_MAX_BYTES) {
    return `${file.name} is ${(file.size / (1024 * 1024)).toFixed(1)} MB. Maximum size is ${QUOTE_ATTACHMENT_MAX_MB} MB.`
  }
  const ext = quoteAttachmentExtension(file.name)
  if ((QUOTE_ATTACHMENT_BLOCKED_EXTENSIONS as readonly string[]).includes(ext)) {
    return `${file.name} cannot be uploaded — HTML, CSS and JavaScript files are not allowed.`
  }
  return null
}

function apiMessage(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (trimmed.startsWith('<')) return null
    return trimmed
  }
  if (Array.isArray(value)) return value.map(apiMessage).find(Boolean) || null
  if (typeof value === 'object') {
    const data = value as Record<string, unknown>
    return apiMessage(data.detail) || apiMessage(data.error) || apiMessage(data.file) || apiMessage(data.non_field_errors)
  }
  return null
}

export function quoteAttachmentUploadErrorMessage(error: unknown, fallback = 'Upload failed.'): string {
  const responseData = (error as { response?: { data?: unknown; status?: number } })?.response?.data
  return apiMessage(responseData) || (error instanceof Error ? error.message : null) || fallback
}

export interface QuoteFile {
  id: string
  file_name?: string
  name?: string
  original_filename?: string
  title?: string
  file_url?: string
  url?: string
  file_size?: number
  file_type?: string
  content_type?: string
  attachment_type?: QuoteFileCategory
  is_pinned?: boolean
  created_at?: string
}

export async function listFiles(orgId: string, quoteId: string): Promise<QuoteFile[]> {
  const res = await qbApi().get(`${base}/${quoteId}/attachments/?org_id=${orgId}`)
  const data = res.data
  if (Array.isArray(data)) return data
  // The quotebuilder attachments endpoint wraps the list as { files: [...] }.
  return data?.files || data?.attachments || data?.results || []
}

/**
 * Fetch a single attachment with a FRESH file_url. The S3 links in the list
 * response are presigned and expire after a short window, so anything that
 * opens/downloads a file must request a fresh URL at click time instead of
 * reusing the (possibly stale) list URL — that staleness is the
 * "Open in new tab → 404 / Access Denied" bug.
 */
export async function getFileFreshUrl(
  orgId: string,
  quoteId: string,
  fileId: string,
): Promise<string | null> {
  const res = await qbApi().get(`${base}/${quoteId}/attachments/${fileId}/?org_id=${orgId}`)
  return res.data?.file_url || res.data?.url || null
}

export async function uploadFile(
  orgId: string,
  quoteId: string,
  file: File,
  category?: QuoteFileCategory,
): Promise<QuoteFile> {
  const form = new FormData()
  form.append('file', file)
  if (category) form.append('attachment_type', category)
  const res = await qbApi().post(`${base}/${quoteId}/attachments/?org_id=${orgId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export interface QDrivePickerFile {
  id: string
  filename: string
  content_type?: string | null
  file_size?: number | null
  uploaded_at?: string | null
  folder_path?: string | null
}

function normalizeQDrivePickerFile(row: unknown): QDrivePickerFile | null {
  if (!row || typeof row !== 'object') return null
  const data = row as Record<string, unknown>
  const source = (data.file && typeof data.file === 'object' ? data.file : data) as Record<string, unknown>
  const id = source.id ?? source.file_id ?? data.id
  const filename = source.filename ?? source.original_filename ?? source.name ?? data.filename ?? data.name
  if (id == null || !filename) return null
  return {
    id: String(id),
    filename: String(filename),
    content_type: (source.content_type ?? source.mime_type ?? source.file_type ?? null) as string | null,
    file_size: typeof source.file_size === 'number' ? source.file_size : typeof source.size === 'number' ? source.size : null,
    uploaded_at: (source.uploaded_at ?? source.created_at ?? null) as string | null,
    folder_path: (source.folder_path ?? source.path ?? source.root_path ?? null) as string | null,
  }
}

function qdrivePickerFilesFromResponse(data: unknown): QDrivePickerFile[] {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : Array.isArray((data as { files?: unknown[] })?.files)
        ? (data as { files: unknown[] }).files
        : []
  return raw.map(normalizeQDrivePickerFile).filter(Boolean) as QDrivePickerFile[]
}

export async function listQDrivePickerFiles(query?: string): Promise<QDrivePickerFile[]> {
  const q = query?.trim()
  if (q) {
    const res = await qbApi().get(`/api/qdrive/search/?q=${encodeURIComponent(q)}`)
    return qdrivePickerFilesFromResponse(res.data)
  }
  const res = await qbApi().get('/api/qdrive/files/?page_size=50&ordering=-uploaded_at')
  return qdrivePickerFilesFromResponse(res.data)
}

export async function downloadQDrivePickerFile(file: QDrivePickerFile): Promise<File> {
  const res = await qbApi().get(`/api/qdrive/files/${file.id}/download/`, { responseType: 'blob' })
  const contentType = String(res.headers?.['content-type'] || '')
  let blob: Blob = res.data

  if (contentType.includes('application/json')) {
    const payload = JSON.parse(await blob.text()) as { url?: string | null }
    if (!payload.url) throw new Error('QDrive did not return a download URL.')
    const fileRes = await fetch(payload.url)
    if (!fileRes.ok) throw new Error('Could not download the selected QDrive file.')
    blob = await fileRes.blob()
  }

  return new File([blob], file.filename, { type: blob.type || file.content_type || undefined })
}

export async function deleteFile(orgId: string, quoteId: string, fileId: string): Promise<void> {
  await qbApi().delete(`${base}/${quoteId}/attachments/${fileId}/?org_id=${orgId}`)
}
